import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AgGridBaseComponent } from '@app/shared/utils/ag-grid-base.component';
import { ColumnTypesEnum } from '@r1-development/rps-shared-ui';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef } from 'ag-grid-community';
import { catchError, forkJoin, map, of } from 'rxjs';
import { Contract } from '@app/shared/models/contract.model';
import { ContractsService } from '@app/shared/services/contracts/contracts.service';

@Component({
  selector: 'app-contract-selection-grid',
  templateUrl: './contract-selection-grid.component.html',
  styleUrls: ['./contract-selection-grid.component.scss'],
  standalone: false,
})
export class ContractSelectionGridComponent extends AgGridBaseComponent<Contract> implements OnInit {
  @ViewChild('contractSelectionGrid') grid: AgGridAngular;
  @Input() healthSystemIds: any[] = [];
  @Output() contractSelection: EventEmitter<Contract> = new EventEmitter<Contract>();

  gridHeight = '400px';
  contracts: Contract[] = [];
  overLayNoRows = `<div class="custom-overlay">Loading Contracts</div>`;

  private selectedRowIndex: number = null;

  columnDefs: ColDef<any, any>[] = [
    {
      field: '',
      headerName: '',
      width: 40,
      checkboxSelection: true,
      suppressFiltersToolPanel: true,
      suppressAutoSize: true,
      suppressHeaderMenuButton: true,
      sortable: false,
      suppressMovable: false,
      filter: false,
      cellClass: 'contract-select-radio-btn',
      cellStyle: { textAlign: 'center', display: 'block' },
    },
    { field: 'healthSystemName', headerName: 'Tenant Name', width: 240 },
    { field: 'contractName', headerName: 'Contract Name', width: 192 },
    { field: 'facility', headerName: 'Facility Name', width: 240 },
    {
      field: 'contractStartDate',
      headerName: 'Start Date',
      type: ColumnTypesEnum.Date,
      width: 144,
    },
    {
      field: 'contractEndDate',
      headerName: 'End Date',
      type: ColumnTypesEnum.Date,
      width: 144,
    },
  ];
  localStoreKey: string = 'contract-selection';

  constructor(private readonly contractService: ContractsService) {
    super();
  }

  ngOnInit(): void {
    this.contractsByHealthSystemId();
  }

  private contractsByHealthSystemId() {
    const contractRequests = this.healthSystemIds.map((hsId) =>
      this.contractService.getAllContractsByHealthSystemId(hsId).pipe(catchError(() => of([])))
    );
    forkJoin(contractRequests)
      .pipe(
        map((contractArrays: Contract[][]) => {
          this.contracts = contractArrays.flat();
        }),
        catchError(() => {
          return of([]);
        })
      )
      .subscribe();
  }

  /**
   *
   * @param event contains data which is the selected contract and node which is the row selected
   * This method is called from the AG Grid rowSelected event.
   * It emits the selected contract when a row is selected and emits null when the selected row is deselected
   * It also does not emit when a different row is selected
   * AG Grid rowSelected event is called for both select and deselect, so we need to check if the row is selected or deselected
   *
   * A case where select a row and then select another row, the first row should be deselected and second row is selected.
   * Then two events are fired, one for deselect and one for select. So added a check not to emit when a different row is unselected.
   */
  protected contractSelected(event: { data: Contract; node: any }): void {
    if (event?.node?.isSelected()) {
      this.selectedRowIndex = event.node?.rowIndex;
      this.contractSelection.emit(event.data);
    } else if (
      (!this.selectedRowIndex || this.selectedRowIndex === event.node?.rowIndex) &&
      !event?.node?.isSelected()
    ) {
      this.contractSelection.emit(null);
    }
  }
}
